import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Plus, Trash2, X, Loader2, Pencil, Tags, CheckCircle2, XCircle, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";
import adminApi from "./adminApi";

const emptyForm = { name: "", status: true };

export default function ManageReservationCategory() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null); // null = "add new"
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");

  const [toast, setToast] = useState(null);
  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 2600);
  };

  const fetchItems = async (pageNo = page) => {
    setLoading(true);
    setError("");
    try {
      const res = await adminApi.get("/admin/reservation-category", { params: { page: pageNo } });
      setItems(res.data.categories || []);
      setTotalPages(res.data.totalPages || 1);
      setTotal(res.data.total || 0);
    } catch (err) {
      setError(err.response?.data?.message || "Could not load reservation categories.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems(page);
    // eslint-disable-next-line
  }, [page]);

  const goToPage = (p) => {
    if (p < 1 || p > totalPages || p === page) return;
    setPage(p);
  };

  // ── Selection ────────────────────────────────────────────────────────────
  const toggleSelectAll = () => {
    setSelected(selected.length === items.length ? [] : items.map((d) => d.rc_id));
  };
  const toggleSelectOne = (id) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  // ── Status toggle ────────────────────────────────────────────────────────
  const handleToggleStatus = async (item, e) => {
    e.stopPropagation();
    const turningOn = !item.status;
    const ok = window.confirm(
      `Are you sure you want to change the status to ${turningOn ? "Active" : "Inactive"}?`
    );
    if (!ok) return;

    try {
      const res = await adminApi.patch(`/admin/reservation-category/${item.rc_id}/status`);
      setItems((prev) =>
        prev.map((d) => (d.rc_id === item.rc_id ? { ...d, status: res.data.status } : d))
      );
    } catch (err) {
      showToast("error", err.response?.data?.message || "Could not update status.");
    }
  };

  // ── Delete (single or multiple, via checkbox) ───────────────────────────
  const handleBulkDelete = async () => {
    if (selected.length === 0) return;
    const ok = window.confirm(
      `Are you sure you want to delete ${selected.length} selected reservation categor${selected.length === 1 ? "y" : "ies"}? This cannot be undone.`
    );
    if (!ok) return;

    setBulkDeleting(true);
    try {
      const res = await adminApi.post("/admin/reservation-category/bulk-delete", { ids: selected });
      showToast("success", res.data.message || `${selected.length} reservation category(ies) deleted.`);
      setSelected([]);
      fetchItems();
    } catch (err) {
      showToast("error", err.response?.data?.message || "Could not delete selected reservation categories.");
    } finally {
      setBulkDeleting(false);
    }
  };

  // ── Add / Edit modal ─────────────────────────────────────────────────────
  const openAddModal = () => {
    setEditingId(null);
    setForm(emptyForm);
    setFormError("");
    setShowModal(true);
  };

  const openEditModal = (item) => {
    setEditingId(item.rc_id);
    setForm({ name: item.name, status: !!item.status });
    setFormError("");
    setShowModal(true);
  };

  const closeModal = () => {
    if (saving) return;
    setShowModal(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");

    if (!form.name.trim()) {
      setFormError("Reservation Category is required.");
      return;
    }

    const payload = { name: form.name.trim(), status: form.status ? "1" : "0" };

    setSaving(true);
    try {
      if (editingId) {
        await adminApi.put(`/admin/reservation-category/${editingId}`, payload);
        showToast("success", "Reservation category updated.");
      } else {
        await adminApi.post("/admin/reservation-category", payload);
        showToast("success", "Reservation category added.");
      }
      setShowModal(false);
      fetchItems();
    } catch (err) {
      setFormError(err.response?.data?.message || "Something went wrong. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const allSelected = items.length > 0 && selected.length === items.length;

  return (
    <div className="h-full flex flex-col">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3 shrink-0 mb-2">
        <div>
          <h3 className="font-display font-bold text-[16px] bg-gradient-to-r from-[#0B2545] to-[#0EA5A4] bg-clip-text text-transparent tracking-tight">
            Manage Reservation Category
          </h3>
        </div>
        <div className="flex items-center gap-2">
          <AnimatePresence>
            {selected.length > 0 && (
              <motion.button
                initial={{ opacity: 0, x: 8 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 8 }}
                onClick={handleBulkDelete}
                disabled={bulkDeleting}
                className="flex items-center gap-1.5 bg-red-50 text-red-600 hover:bg-red-100 text-[13px] font-medium px-3 py-1.5 rounded-lg transition disabled:opacity-60"
              >
                {bulkDeleting ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />}
                Delete Selected ({selected.length})
              </motion.button>
            )}
          </AnimatePresence>
          <button
            onClick={openAddModal}
            className="flex items-center gap-1.5 bg-[#0EA5A4] hover:bg-[#0B7E7D] text-white text-[13px] font-medium px-3 py-1.5 rounded-lg transition shadow-sm"
          >
            <Plus size={15} />
            Add New Reservation Category
          </button>
        </div>
      </div>

      {/* Table card */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm flex-1 min-h-0 flex flex-col overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-slate-400 text-sm gap-2">
            <Loader2 size={18} className="animate-spin" /> Loading reservation categories...
          </div>
        ) : error ? (
          <div className="py-16 text-center text-sm text-red-500">{error}</div>
        ) : items.length === 0 ? (
          <div className="py-16 text-center">
            <Tags size={28} className="mx-auto text-slate-300 mb-2" />
            <p className="text-sm text-slate-400">No reservation categories yet — click "Add New Reservation Category" to create one.</p>
          </div>
        ) : (
          <div className="flex-1 min-h-0 overflow-auto">
            <table className="w-full text-sm border-collapse">
              <thead className="sticky top-0 z-10 shadow-md">
                <tr className="text-left text-sm">
                  <th className="th-colorful pl-3 pr-2 py-1.5 w-8 border-b-2 border-r normal-case">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleSelectAll}
                      className="w-4 h-4 rounded border-slate-300 accent-[#0EA5A4] cursor-pointer"
                    />
                  </th>
                  <th className="th-colorful px-3 py-1.5 font-bold uppercase tracking-wide border-b-2 border-r">Reservation Category</th>
                  <th className="th-colorful px-3 py-1.5 font-bold uppercase tracking-wide text-center border-b-2">Status</th>
                </tr>
              </thead>
              <tbody>
                <AnimatePresence initial={false}>
                  {items.map((d, i) => (
                    <motion.tr
                      key={d.rc_id}
                      layout
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.2, delay: i * 0.02 }}
                      onClick={() => openEditModal(d)}
                      className={`cursor-pointer hover:bg-[#0EA5A4]/30 transition-colors duration-150 border-b border-slate-200 ${
                        selected.includes(d.rc_id) ? "bg-[#0EA5A4]/15" : ""
                      }`}
                    >
                      <td className="pl-3 pr-2 py-2 border-r border-slate-200" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selected.includes(d.rc_id)}
                          onChange={() => toggleSelectOne(d.rc_id)}
                          className="w-4 h-4 rounded border-slate-300 accent-[#0EA5A4] cursor-pointer"
                        />
                      </td>
                      <td className="px-3 py-2 text-slate-800 font-medium border-r border-slate-200">{d.name}</td>
                      <td className="px-3 py-2">
                        <div className="flex items-center justify-center">
                          <motion.button
                            whileTap={{ scale: 0.9 }}
                            onClick={(e) => handleToggleStatus(d, e)}
                            title={d.status ? "Active — click to deactivate" : "Inactive — click to activate"}
                            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition ${
                              d.status
                                ? "bg-[#0EA5A4]/10 text-[#0B7E7D] hover:bg-[#0EA5A4]/20"
                                : "bg-slate-100 text-slate-400 hover:bg-slate-200"
                            }`}
                          >
                            {d.status ? <CheckCircle2 size={15} /> : <XCircle size={15} />}
                            {d.status ? "Active" : "Inactive"}
                          </motion.button>
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </AnimatePresence>
              </tbody>
            </table>
          </div>
        )}

        {!loading && !error && totalPages > 1 && (
          <div className="shrink-0 flex items-center justify-between border-t border-slate-200 px-5 py-2">
            <span className="text-xs text-slate-400">
              Page {page} of {totalPages}
            </span>
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => goToPage(1)}
                disabled={page <= 1}
                title="First page"
                className="flex items-center justify-center w-7 h-7 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-40 disabled:hover:bg-transparent transition"
              >
                <ChevronsLeft size={16} />
              </button>
              <button
                onClick={() => goToPage(page - 1)}
                disabled={page <= 1}
                title="Previous page"
                className="flex items-center justify-center w-7 h-7 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-40 disabled:hover:bg-transparent transition"
              >
                <ChevronLeft size={16} />
              </button>
              <button
                onClick={() => goToPage(page + 1)}
                disabled={page >= totalPages}
                title="Next page"
                className="flex items-center justify-center w-7 h-7 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-40 disabled:hover:bg-transparent transition"
              >
                <ChevronRight size={16} />
              </button>
              <button
                onClick={() => goToPage(totalPages)}
                disabled={page >= totalPages}
                title="Last page"
                className="flex items-center justify-center w-7 h-7 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-40 disabled:hover:bg-transparent transition"
              >
                <ChevronsRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Toast */}
      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className={`fixed bottom-6 right-6 z-50 px-4 py-3 rounded-lg shadow-lg text-sm font-medium ${
              toast.type === "success" ? "bg-[#0B2545] text-white" : "bg-red-600 text-white"
            }`}
          >
            {toast.message}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add / Edit modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeModal}
            className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 12 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
            >
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
                <h4 className="font-display font-semibold text-[#0B2545]">
                  {editingId ? "Edit Reservation Category" : "Add New Reservation Category"}
                </h4>
                <button onClick={closeModal} className="text-slate-400 hover:text-slate-700">
                  <X size={18} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
                <div>
                  <label className="text-xs font-medium text-slate-500 mb-1.5 block">
                    Reservation Category <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="e.g. General"
                    className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#0EA5A4]/30 focus:border-[#0EA5A4] transition"
                  />
                </div>

                <label className="hidden">
                  <input
                    type="checkbox"
                    checked={form.status}
                    onChange={(e) => setForm({ ...form, status: e.target.checked })}
                    className="w-4 h-4 rounded border-slate-300 accent-[#0EA5A4] cursor-pointer"
                  />
                  <span className="text-sm text-slate-600">Active</span>
                </label>

                {formError && (
                  <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                    {formError}
                  </p>
                )}

                <div className="flex items-center gap-3 pt-2">
                  <button
                    type="submit"
                    disabled={saving}
                    className="flex-1 flex items-center justify-center gap-2 bg-[#0B2545] hover:bg-[#143A63] text-white font-medium rounded-lg py-2.5 transition disabled:opacity-60"
                  >
                    {saving ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : editingId ? (
                      <Pencil size={15} />
                    ) : (
                      <Plus size={15} />
                    )}
                    {saving ? "Saving..." : editingId ? "Update" : "Submit"}
                  </button>
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-4 py-2.5 rounded-lg text-sm font-medium text-slate-500 hover:bg-slate-50 transition"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
